import { cn } from '@simplywallst/ui-core/next';

export const IMAGE_QUALITY = {
  placeholder: 1, // Keep very low quality for placeholders
} as const;

// CSS classes for styling
export const CSS_CLASSES = {
  container: {
    blurred: cn(
      'pointer-events-none absolute inset-0 light:hidden lg:rounded-b-md',
      '[-webkit-mask-image:linear-gradient(rgba(0,0,0,0)_0%,rgba(0,0,0,.15)_25%,rgba(0,0,0,0)_100%)]',
      'md:[-webkit-mask-image:linear-gradient(60deg,rgba(0,0,0,.2)_15%,rgba(0,0,0,0)_100%)]'
    ),
    normal: '',
    content: 'w-full h-auto',
  },
  placeholder: {
    blurred: 'absolute inset-0 object-cover blur-sm',
    normal: 'absolute inset-0 object-cover',
    content: 'w-full h-auto object-cover',
  },
  mainImage: {
    blurred: 'z-1 size-full object-cover blur-md',
    normal: 'z-1 size-full object-cover',
    content: 'w-full h-auto object-cover',
  },
  noImage:
    'pointer-events-none absolute inset-0 bg-gradient-to-tr from-legacy-spacegray to-legacy-noir lg:rounded-b-md',
} as const;

// Helper functions
export const hasValidImage = (src: string): boolean => {
  return Boolean(src && src.trim() !== '');
};

// Responsive configuration for header background images
export const RESPONSIVE_CONFIG = {
  mobile: { width: 400, height: 44, blur: 60, quality: 10 },
  tablet: { width: 600, height: 65, blur: 60, quality: 15 },
  desktop: { width: 757, height: 82, blur: 60, quality: 20 },
} as const;

// Responsive configuration for content images (optimized for embedded content)
// Following Cloudflare's recommended sizes: 1920px desktop, 960px tablet, 640px mobile
// Including DPR-aware breakpoints to prevent larger images on high-DPR devices
export const CONTENT_RESPONSIVE_CONFIG = {
  mobile: {
    width: 640,
    quality: 75,
    fit: 'scale-down' as const,
    sharpen: 1,
    slowConnectionQuality: 50,
    // DPR-aware: 640px * 2 = 1280px for 2x devices, 640px * 3 = 1920px for 3x devices
    dpr: 1,
  },
  tablet: {
    width: 960,
    quality: 80,
    fit: 'scale-down' as const,
    sharpen: 1,
    slowConnectionQuality: 60,
    dpr: 1,
  },
  desktop: {
    width: 1920,
    quality: 85,
    fit: 'scale-down' as const,
    sharpen: 1,
    slowConnectionQuality: 70,
    dpr: 1,
  },
  large: {
    width: 2560,
    quality: 85,
    fit: 'scale-down' as const,
    sharpen: 1,
    slowConnectionQuality: 70,
    dpr: 1,
  },
} as const;

// DPR-aware breakpoints for high-DPI devices
// Following Cloudflare's recommendations: https://developers.cloudflare.com/images/transform-images/make-responsive-images/
export const CONTENT_DPR_BREAKPOINTS = {
  mobile: [
    { width: 640, dpr: 1, quality: 75 },
    { width: 960, dpr: 1.5, quality: 75 }, // More conservative for mobile
    { width: 1280, dpr: 2, quality: 75 }, // 640 * 2
  ],
  tablet: [
    { width: 960, dpr: 1, quality: 80 },
    { width: 1440, dpr: 1.5, quality: 80 }, // More conservative for tablet
    { width: 1920, dpr: 2, quality: 80 }, // 960 * 2
  ],
  desktop: [
    { width: 1920, dpr: 1, quality: 85 },
    { width: 2560, dpr: 1.5, quality: 85 }, // More conservative for desktop
  ],
} as const;

export const createResponsiveBreakpoints = (includeBlur = true) => [
  // Mobile
  {
    width: RESPONSIVE_CONFIG.mobile.width,
    height: RESPONSIVE_CONFIG.mobile.height,
    quality: RESPONSIVE_CONFIG.mobile.quality,
    ...(includeBlur && { blur: RESPONSIVE_CONFIG.mobile.blur }),
  },
  // Tablet
  {
    width: RESPONSIVE_CONFIG.tablet.width,
    height: RESPONSIVE_CONFIG.tablet.height,
    quality: RESPONSIVE_CONFIG.tablet.quality,
    ...(includeBlur && { blur: RESPONSIVE_CONFIG.tablet.blur }),
  },
  // Desktop
  {
    width: RESPONSIVE_CONFIG.desktop.width,
    height: RESPONSIVE_CONFIG.desktop.height,
    quality: RESPONSIVE_CONFIG.desktop.quality,
    ...(includeBlur && { blur: RESPONSIVE_CONFIG.desktop.blur }),
  },
];

// Content-specific breakpoints (no height constraint, optimized for content)
export const createContentBreakpoints = () => [
  // Mobile
  {
    width: CONTENT_RESPONSIVE_CONFIG.mobile.width,
    quality: CONTENT_RESPONSIVE_CONFIG.mobile.quality,
  },
  // Tablet
  {
    width: CONTENT_RESPONSIVE_CONFIG.tablet.width,
    quality: CONTENT_RESPONSIVE_CONFIG.tablet.quality,
  },
  // Desktop
  {
    width: CONTENT_RESPONSIVE_CONFIG.desktop.width,
    quality: CONTENT_RESPONSIVE_CONFIG.desktop.quality,
  },
  // Large screens
  {
    width: CONTENT_RESPONSIVE_CONFIG.large.width,
    quality: CONTENT_RESPONSIVE_CONFIG.large.quality,
  },
];
